import { ReactElement, SyntheticEvent, useMemo, useState } from 'react';
import { Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { Styles } from 'common/types/styles';
import {
  usePDPHistoryCategoriesQuery,
  usePDPHistoryV1Query,
  usePDPHistoryV2Query,
} from 'common/hooks/api/queries/use-content-query';
import { PdpHistoryContentV2 } from './pdp-history-content-v2';
import { PdpHistoryContent } from './pdp-history-content';

const styles: Styles = {
  subtabs: { minHeight: 36, mb: 1 },
  subtab: { minHeight: 36, textTransform: 'none', fontSize: 13 },
  loaderWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    gap: 2,
  },
};

const PAGE_SIZE = 10;

interface Props {
  pid: string;
}

export function TargetHistoryTabs({ pid }: Props): ReactElement {
  const { data: categoriesData, isLoading: isCategoriesLoading } = usePDPHistoryCategoriesQuery(true);
  const [selectedSubtab, setSelectedSubtab] = useState(0);
  const [page, setPage] = useState(0);

  const categories = categoriesData?.all ?? [];
  const selectedCategory = categories[selectedSubtab];

  const filters = useMemo(
    () => ({
      productID: pid,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      category: selectedCategory?.id ?? '',
    }),
    [pid, page, selectedCategory?.id]
  );

  const { data: historyDataV1, isFetching: isHistoryV1Loading } = usePDPHistoryV1Query(
    filters,
    Boolean(selectedCategory?.id) && selectedCategory?.version === 'v1'
  );

  const { data: historyDataV2, isFetching: isHistoryV2Loading } = usePDPHistoryV2Query(
    filters,
    Boolean(selectedCategory?.id) && selectedCategory?.version !== 'v1'
  );

  const handleSubtabChange = (_: SyntheticEvent, value: number) => {
    setSelectedSubtab(value);
    setPage(0);
  };

  // Select data based on category version
  const isV1Category = selectedCategory?.version === 'v1';

  if (isCategoriesLoading) {
    return (
      <Box sx={styles.loaderWrapper}>
        <CircularProgress size={14} />
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Tabs
        value={selectedSubtab}
        onChange={handleSubtabChange}
        sx={styles.subtabs}
        variant="scrollable"
        scrollButtons="auto"
      >
        {categories.map((cat, i) => (
          <Tab key={cat.id} value={i} label={`${cat.displayName} (${cat.version})`} sx={styles.subtab} />
        ))}
      </Tabs>
      {isV1Category ? (
        <PdpHistoryContent
          history={historyDataV1?.items ?? null}
          isHistoryLoading={isHistoryV1Loading}
          total={historyDataV1?.total}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          skipSort
        />
      ) : (
        <PdpHistoryContentV2
          history={historyDataV2?.items ?? null}
          isHistoryLoading={isHistoryV2Loading}
          total={historyDataV2?.total}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      )}
    </Box>
  );
}
